import PortalDialog, {PortalDialogProperties, PortalDialogState} from "../PortalDialog";
import React, {ReactNode} from "react";
import {Dialog} from "primereact/dialog";
import PortalDialogType from "../../PortalDialogType";
import {Button} from "primereact/button";
import Parameter from "../../parameter/Parameter";
import PortalDocument from "../../../document/PortalDocument";
import DialogDefault from "../../parameter/PortalDialogDefault";
import FileHelper, {PATH_SEPARATOR} from "../../../../shared/helper/FileHelper";
import PortfolioAddDialogFields from "../../field/portfolio/PortfolioAddDialogFields";
import MimeType from "../../../document/mimetype/MimeType";
import MimeTypeFilter from "../../../document/mimetype/MimeTypeFilter";
import MimeTypes from "../../../document/mimetype/MimeTypes";
import {InputText} from "primereact/inputtext";
import PortalDocumentManager from "../../../document/PortalDocumentManager";
import {WebServiceType, WebServiceTypes} from "@softvision/webpdf-wsclient-9-typescript";
import Message from "../../../../shared/log/Message";
import {MessageType} from "../../../../shared/log/MessageInterface";
import {
	AdminWebserviceStatus,
	BaseToolbox,
	Functions,
	Information,
	PortfolioFolder,
	Security,
	Settings,
	SettingsInterface,
	SignatureEditProfile, ToolboxPortfolio
} from "@softvision/webpdf-wsclient-9-typescript/lib/generated-sources";
import {PortalToolboxPortfolioPortfolio} from "../../../../shared/models/PortalToolboxPortfolioPortfolio";
import {PortalPortfolioAdd} from "../../../../shared/models/PortalPortfolioAdd";
import PortalDialogSecurity, {Intention} from "../../PortalDialogSecurity";
import Translate from "../../../../shared/translation/Translate";
import Helper from "../../../../shared/components/Helper";
import Icon from "../../../../shared/components/Icon";
import FunctionHelper from "../../../../shared/helper/FunctionHelper";

export class PortfolioAddFolderDialogDefinition extends PortalDialog<PortalToolboxPortfolioPortfolio,
	PortalDialogProperties<PortalToolboxPortfolioPortfolio>, PortalDialogState<PortalToolboxPortfolioPortfolio>> {
	public static isEnabled(portalInfo: Information): boolean {
		return FunctionHelper.applyValue(
			portalInfo.webserviceStatus?.webservices?.toolbox?.enabled, AdminWebserviceStatus.getEnabledDefault()
		) && FunctionHelper.applyValue(
			portalInfo.functionsStatus?.portfolio, Functions.getPortfolioDefault()
		);
	}

	public static isActive(
		selectedDocuments: Array<PortalDocument>, portalInfo: Information, signatureEditProfile: SignatureEditProfile
	): boolean {
		let mimeTypes: Array<MimeType> = [];

		for (let selectedDocument of selectedDocuments) {
			mimeTypes.push(selectedDocument.getMimeType());

			if (selectedDocument.isLocked()) {
				return false;
			}

			if (!PortalDialogSecurity.checkRestrictions(
				PortalDialogType.PORTFOLIO_ADD_FOLDER, signatureEditProfile, Intention.editingContent, selectedDocument
			)) {
				return false;
			}

			if (!selectedDocument.isEditable({checkPortfolio: false})) {
				return false;
			}
		}

		return this.isEnabled(portalInfo) && selectedDocuments.length === 1 &&
			MimeTypeFilter.checkSelection(mimeTypes, [MimeTypes.PDF]);
	}

	public renderContents(): ReactNode {
		let parameter: PortalToolboxPortfolioPortfolio | undefined = this.state.parameter?.data;
		let errors: Map<PortfolioAddDialogFields, string> = this.state.fieldErrors;

		if (!FunctionHelper.exists(parameter?.add)) {
			return null;
		}

		let addParameter: PortalPortfolioAdd = parameter!.add as PortalPortfolioAdd;

		return (
			<>
				<fieldset className="p-col-12 p-grid">
					<div className="p-col-12 p-grid">
						<label className="p-col-12 p-sm-4" htmlFor="control-foldername">
							<Translate {...this.props} data="portfolio.add.foldername" namespace="dialog"/>
						</label>
						<div className="p-col-12 p-sm-8 p-grid">
							<InputText id="control-foldername" autoFocus={true}
									   className={
										   errors.has(PortfolioAddDialogFields.FOLDERNAME) ? "p-invalid" : ""
									   }
									   tooltip={errors.has(PortfolioAddDialogFields.FOLDERNAME) ? Translate.value(
										   errors.get(PortfolioAddDialogFields.FOLDERNAME)!, {ns: "message"}
									   ) : ""}
									   value={FunctionHelper.applyValue(
										   addParameter?.fileName, PortalPortfolioAdd.getFileNameDefault()
									   )}
									   onInput={(e) => this.onChange(
										   PortfolioAddDialogFields.FOLDERNAME, e.currentTarget.value
									   )}/>
							{this.renderFieldError(errors.has(PortfolioAddDialogFields.FOLDERNAME) ?
								Translate.value(
									errors.get(PortfolioAddDialogFields.FOLDERNAME)!, {ns: "message"}
								) : ""
							)}
						</div>
					</div>
				</fieldset>
			</>
		);
	}

	public renderDialog(): ReactNode {
		let classList: Array<string> = ["portfolio-add-folder-dialog"];
		if (this.state.submitted) {
			classList.push("submitted");
		}

		return (
			<Dialog className={classList.join(" ")} visible={this.props.visible} onHide={this.hide}
					resizable={false} closeOnEscape={true} header={this.renderHeader()} footer={this.renderFooter()}
					appendTo={document.getElementById("root")}>
				<div className="p-grid">
					{this.renderError()}
					<Helper {...this.props}>
						<Translate {...this.props} data={"dialog.portfolio.add.folder"} namespace={"help"}
								   markdown={true}/>
					</Helper>
					{this.renderContents()}
				</div>
			</Dialog>
		);
	}

	protected onChange(field: PortfolioAddDialogFields, value: any): void {
		let tempParameter: PortalToolboxPortfolioPortfolio = this.state.parameter!.data;
		let addParameter: PortalPortfolioAdd = tempParameter.add as PortalPortfolioAdd;
		let errors: Map<PortfolioAddDialogFields, string> = this.state.fieldErrors;

		switch (field) {
			case PortfolioAddDialogFields.FOLDERNAME:
				if (!FunctionHelper.exists(value) || FunctionHelper.isEmpty(value)) {
					errors.set(field, "errors.fields.set");
				} else if (!FileHelper.isFilename(value)) {
					errors.set(field, "errors.fields.filename");
				} else if (errors.has(field)) {
					errors.delete(field);
				}

				addParameter.fileName = value;
				break;
		}

		this.setState({
			parameter: this.state.parameter
		});
	}

	protected renderHeader(): ReactNode {
		return (
			<div>
				<h1>
					<Icon icon={"icon-add small"}/>
					<Translate {...this.props} data="portfolio.add.title.folder" namespace="dialog"/>
				</h1>
				<Button icon="icon icon-close" onClick={() => this.hide()}/>
				{this.renderHelpButton()}
			</div>
		);
	}

	protected async submit(): Promise<void> {
		await super.submit();

		let parameter: PortalToolboxPortfolioPortfolio = this.state.parameter!.data.clone();
		let addParameter: PortalPortfolioAdd = parameter.add as PortalPortfolioAdd;

		let folderDataType: PortfolioFolder = PortfolioFolder.fromJson({
			path: addParameter.path + PATH_SEPARATOR + addParameter.fileName
		});

		addParameter.folder = [folderDataType];

		let toolboxOperation: ToolboxPortfolio = BaseToolbox.fromJson({
			portfolio: parameter.toJson()
		});

		toolboxOperation.portfolio!.add!.initialFileName = undefined;
		toolboxOperation.portfolio!.add!.initialDocumentPath = undefined;

		let operationData: Array<BaseToolbox> = [toolboxOperation];
		let webServiceType: WebServiceType = WebServiceTypes.TOOLBOX;
		let documentManager: PortalDocumentManager = this.props.context.documentManager;
		let selection: Array<PortalDocument> = documentManager.getSelection();

		for (let portalDocument of selection) {
			try {
				await this.props.context.session.executeRequest({
					webServiceType: webServiceType,
					document: portalDocument,
					operation: operationData,
					settings: Settings.fromJson({
						pdfa: {
							pdfaEditProfile: this.props.pdfaEditProfile
						},
						signature: {
							signatureEditProfile: this.props.signatureEditProfile
						},
						compress: {
							compressProfile: this.props.compressProfile,
							compressObjects: {
								objectStreamSize: this.props.objectStreamSize
							}
						}
					} as SettingsInterface)
				});
			} catch (exception: any) {
				if (await this.onError(exception)) {
					await this.submit();
				}
				return;
			}


			let message: Message = new Message("success.toolbox.portfolio.add.folder", MessageType.SUCCESS);
			this.props.context.getPage().addMessage(message);
		}

		this.hide();
	}

	protected isValid(): boolean {
		let tempParameter: PortalToolboxPortfolioPortfolio = this.state.parameter!.data;
		let addParameter: PortalPortfolioAdd = tempParameter.add as PortalPortfolioAdd;

		if (addParameter.fileName === "") {
			return false;
		}

		return super.isValid();
	}

	protected isEditPasswordNeeded(portalDocument: PortalDocument): boolean {
		return super.isEditPasswordNeeded(portalDocument) && !FunctionHelper.applyValue(
			portalDocument.metadata?.security?.canModify, Security.getCanModifyDefault()
		);
	}

	/**
	 * Handle showing/initializing the dialog
	 */
	protected async onShow(): Promise<void> {
		await super.onShow();

		let parameter: PortalToolboxPortfolioPortfolio | undefined = this.state.parameter?.data;

		if (!FunctionHelper.exists(parameter?.add)) {
			return;
		}

		let addParameter: PortalPortfolioAdd = parameter!.add as PortalPortfolioAdd;

		// clear old data
		addParameter.folder = [];

		this.setState({
			parameter: this.state.parameter,
			defaultParameter: this.state.parameter?.clone(),
		});
	};

	protected getDialogType(): PortalDialogType {
		return PortalDialogType.PORTFOLIO_ADD_FOLDER;
	}

	protected getDefaultParameter(): Parameter<PortalToolboxPortfolioPortfolio> {
		return DialogDefault.getParameter(
			this.getDialogType(), this.props.context.documentManager.getSelection()
		);
	}
}

const PortfolioAddFolderDialog = React.memo(PortfolioAddFolderDialogDefinition,
	(
		prevProps: Readonly<PortalDialogProperties<PortalToolboxPortfolioPortfolio>>,
		nextProps: Readonly<PortalDialogProperties<PortalToolboxPortfolioPortfolio>>
	) => {
		if (!nextProps.visible && !prevProps.visible) {
			return true
		}

		return false;
	}
);

export default PortfolioAddFolderDialog;